//this file is ModificationHistoryModal — clinician record modification history
// (Phase 1: read-only diff timeline over the version-history read model).

import React, { useEffect, useState, useContext } from 'react';
import moment from 'moment';
import axios from 'src/axios';

import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { formatValue, isMono } from './recordHistoryFormatters';

// ─── Design system ────────────────────────────────────────────────────────────
const GREEN = '#1D9E75'; // new / added (brand)
const PINK = '#B23A6B'; // old / removed (muted)
const MUTED = '#6b7a8d'; // tertiary
const NAVY = '#1f2a37';

const DiffValue = ({ type, value, color, strike }) => {
  const text = formatValue(type, value);
  const empty = text == null;
  return (
    <Box
      component="span"
      sx={{
        color: empty ? MUTED : color,
        fontStyle: empty ? 'italic' : 'normal',
        textDecoration: strike && !empty ? 'line-through' : 'none',
        fontFamily: isMono(type) ? 'monospace' : 'inherit',
        fontSize: 13
      }}
    >
      {empty ? 'empty' : text}
    </Box>
  );
};

const ChangeRow = ({ change }) => {
  const unchanged =
    JSON.stringify(change.oldValue) === JSON.stringify(change.newValue);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 1.5,
        py: 0.5,
        flexWrap: 'wrap'
      }}
    >
      <Typography sx={{ fontSize: 13, color: NAVY, minWidth: 150 }}>
        {change.label}
      </Typography>
      {unchanged ? (
        <Box component="span" sx={{ fontSize: 13 }}>
          <DiffValue type={change.type} value={change.newValue} color={NAVY} />
          <Box component="span" sx={{ color: MUTED, ml: 1 }}>
            (unchanged)
          </Box>
        </Box>
      ) : (
        <Box component="span">
          <DiffValue
            type={change.type}
            value={change.oldValue}
            color={PINK}
            strike
          />
          <Box component="span" sx={{ color: MUTED, mx: 1 }}>
            →
          </Box>
          <DiffValue type={change.type} value={change.newValue} color={GREEN} />
        </Box>
      )}
    </Box>
  );
};

const MigrationBanner = ({ members }) => (
  <Box
    sx={{
      border: '0.5px solid',
      borderColor: 'divider',
      borderRadius: '12px',
      px: 2,
      py: 1
    }}
  >
    {members.map((m, i) => (
      <Box
        key={i}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
      >
        <Typography sx={{ fontSize: 13, fontFamily: 'monospace', color: PINK }}>
          {m.from}
        </Typography>
        <Typography sx={{ fontSize: 13, color: MUTED }}>→</Typography>
        <Typography
          sx={{ fontSize: 13, fontFamily: 'monospace', color: GREEN }}
        >
          {m.to}
        </Typography>
      </Box>
    ))}
  </Box>
);

const TimelineEntry = ({ entry, isLast }) => (
  <Box sx={{ display: 'flex', gap: 1.5 }}>
    {/* rail + dot */}
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 0.5
      }}
    >
      <Box
        sx={{
          width: 11,
          height: 11,
          borderRadius: '50%',
          bgcolor: entry.isCurrent
            ? GREEN
            : entry.isCreation
            ? '#fff'
            : '#c4ccd6',
          border: entry.isCreation ? `2px solid #c4ccd6` : 'none',
          flexShrink: 0
        }}
      />
      {!isLast && (
        <Box sx={{ width: '2px', flex: 1, bgcolor: '#e3e8ef', mt: 0.5 }} />
      )}
    </Box>

    {/* content */}
    <Box sx={{ flex: 1, pb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: NAVY }}>
          {entry.title}
        </Typography>
        {entry.entityLabel && (
          <Typography sx={{ fontSize: 12, color: MUTED }}>
            · {entry.entityLabel}
          </Typography>
        )}
        {entry.isCurrent && (
          <Chip
            label="Current"
            size="small"
            sx={{
              bgcolor: `${GREEN}1A`,
              color: GREEN,
              fontWeight: 600,
              fontSize: 11,
              height: 20
            }}
          />
        )}
      </Box>
      <Typography sx={{ fontSize: 12, color: MUTED, mb: 1 }}>
        {entry.actor?.name}
        {entry.actor?.role ? ` · ${entry.actor.role}` : ''}
        {' · '}
        {moment(entry.timestamp).format('D MMM YYYY, h:mm a')}
      </Typography>

      {entry.isMigration && entry.migratedMembers?.length > 0 && (
        <MigrationBanner members={entry.migratedMembers} />
      )}

      {!entry.isMigration && entry.changes && entry.changes.length > 0 && (
        <Box
          sx={{
            border: '0.5px solid',
            borderColor: 'divider',
            borderRadius: '12px',
            px: 2,
            py: 1
          }}
        >
          {entry.changes.map((c, i) => (
            <ChangeRow key={`${c.field}-${i}`} change={c} />
          ))}
        </Box>
      )}
    </Box>
  </Box>
);

const ModificationHistoryModal = (props) => {
  // recordId (optional) scopes the timeline to ONE record — e.g. a single
  // clinical note's own history, opened from inside its editor.
  // familyId (instead of familyMemberId) drives family-scoped record types
  // (PEDIGREE_CHART — the structural pedigree timeline).
  const { modificationType, familyMemberId, familyId, recordId } = props;
  const [loading, setLoading] = useState(false);
  const { setSnack } = useContext(SnackContext);
  const [entries, setEntries] = useState([]);
  const [noteTypeFilter, setNoteTypeFilter] = useState('ALL');

  useEffect(() => {
    if (!modificationType || (!familyMemberId && !familyId)) return;
    setLoading(true);
    axios
      .get(APICONSTANTS.getRecordHistory, {
        params: {
          recordType: modificationType,
          ...(familyMemberId ? { familyMemberId } : {}),
          ...(familyId ? { familyId } : {}),
          ...(recordId ? { recordId } : {})
        }
      })
      .then((res) => setEntries(res.data?.entries || []))
      .catch(() => {
        setSnack({
          status: true,
          msg: 'An error occurred while fetching modification history',
          severity: 'error'
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyMemberId, familyId, modificationType, recordId]);

  // The aggregate Clinical History mixes every note type; offer a type filter
  // there (but not for a single-record view, where there's only one type).
  const isClinicalAggregate =
    modificationType === CONSTANTS.auditLogType.clinicalRecord && !recordId;
  const noteTypes = isClinicalAggregate
    ? Array.from(
        new Set(entries.map((e) => e.collectionName).filter(Boolean))
      ).sort()
    : [];
  const visibleEntries =
    isClinicalAggregate && noteTypeFilter !== 'ALL'
      ? entries.filter((e) => e.collectionName === noteTypeFilter)
      : entries;

  // Single-record view: name the record the timeline covers (e.g. the disease
  // or gene), since the dialog title is just "Record History".
  const scopedLabel = recordId ? visibleEntries[0]?.entityLabel : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {scopedLabel && (
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: NAVY, mb: 1 }}>
          {scopedLabel}
        </Typography>
      )}
      {noteTypes.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <Select
              value={noteTypeFilter}
              onChange={(e) => setNoteTypeFilter(e.target.value)}
              sx={{ fontSize: 13 }}
            >
              <MenuItem value="ALL" sx={{ fontSize: 13 }}>
                All record types
              </MenuItem>
              {noteTypes.map((t) => (
                <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {!visibleEntries.length ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ color: MUTED, fontSize: 14 }}>
            No modification history to show for this record yet.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ py: 1, maxHeight: '60vh', overflowY: 'auto' }}>
          {visibleEntries.map((entry, i) => (
            <TimelineEntry
              key={`${entry.recordId}-${entry.timestamp}-${i}`}
              entry={entry}
              isLast={i === visibleEntries.length - 1}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ModificationHistoryModal;
